import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import type { WorkflowConfig } from 'sprites-types/workflow/core'
import type { RenderingConfig } from 'sprites-types/workflow/tools/common'
import { DynamicContentRenderer } from '../shared/DynamicContentRenderer'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import {
  csvToMarkdownTable,
  extractRenderingSchema,
  inferRawPresentation,
  inferredContentToMarkdown,
  processRawOutputs,
  resolveListTemplateVariables,
  resolveTemplateVariables,
} from '../shared/renderingUtils'

function NativeCodeRunnerDisplayComponent({
  nodeExecution,
  workflowConfig,
  onOpenPanel,
}: WorkflowToolComponentProps) {
  const renderingSchema = extractRenderingSchema(nodeExecution)

  // If no rendering schema provided, show smart raw outputs
  if (!renderingSchema || renderingSchema.length === 0) {
    const inferredList = processRawOutputs(nodeExecution, workflowConfig)

    return (
      <div className="space-y-4">
        {inferredList.map((inferred, idx) => (
          <DynamicContentRenderer
            key={idx}
            type={inferred.type}
            content={inferred.content}
            outputs={nodeExecution.outputs}
            nodeId={nodeExecution.nodeId}
            onOpenPanel={onOpenPanel}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {renderingSchema.map((config, index) => (
        <DynamicContentRenderer
          key={index}
          type={config.type}
          content={config.content}
          outputs={nodeExecution.outputs}
          nodeId={nodeExecution.nodeId}
          onOpenPanel={onOpenPanel}
        />
      ))}
    </div>
  )
}

// Download function that creates a comprehensive document with all rendered content
function downloadCodeRunnerResponse(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

// Convert Code Runner response to markdown
function nativeCodeRunnerToMarkdown(
  nodeExecution: NodeExecution,
  workflowConfig?: WorkflowConfig
): string {
  try {
    const renderingSchema = extractRenderingSchema(nodeExecution)
    const sections: string[] = ['## Code Runner\n']

    // If no rendering schema provided, use the shared logic
    if (!renderingSchema || renderingSchema.length === 0) {
      const inferredList = processRawOutputs(nodeExecution, workflowConfig)

      if (inferredList.length === 0) {
        // No variables found, process the raw outputs directly
        const inferred = inferRawPresentation(nodeExecution.outputs)
        sections.push(inferredContentToMarkdown(inferred))
      } else {
        // Process each inferred output
        inferredList.forEach((inferred, index) => {
          if (index > 0) sections.push('\n---\n')
          sections.push(inferredContentToMarkdown(inferred))
        })
      }
    } else {
      // Use rendering schema - resolve template variables in each config
      const resolvedRenderingConfigs = renderingSchema.map((config: RenderingConfig) => ({
        ...config,
        content:
          config.type === 'list'
            ? resolveListTemplateVariables(
                config.content,
                nodeExecution.outputs,
                nodeExecution.nodeId
              )
            : resolveTemplateVariables(config.content, nodeExecution.outputs, nodeExecution.nodeId),
      }))

      resolvedRenderingConfigs.forEach((config, index) => {
        if (index > 0) sections.push('\n---\n')

        switch (config.type) {
          case 'markdown':
            sections.push(config.content)
            break
          case 'list':
            sections.push(config.content)
            break
          case 'html':
            sections.push('```html')
            sections.push(config.content)
            sections.push('```')
            break
          case 'csv':
            sections.push(csvToMarkdownTable(config.content))
            break
          case 'url':
            sections.push(`[${config.content}](${config.content})`)
            break
          default:
            sections.push('```')
            sections.push(config.content)
            sections.push('```')
        }
      })
    }

    sections.push('\n---\n')
    return sections.join('\n')
  } catch (error) {
    return `## Code Runner\n\nError parsing results: ${error}\n\n---\n`
  }
}

// Export the component with markdown support
export const NativeCodeRunnerDisplay = createWorkflowToolComponent(
  NativeCodeRunnerDisplayComponent,
  downloadCodeRunnerResponse,
  nativeCodeRunnerToMarkdown
)
